import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';

import amplitude from 'amplitude-js';
import { updateMessages } from 'apps/auth/actions/appAction';
import { addExtras, updateExtraByName } from 'apps/auth/actions/extraAction';
import { changeFormFieldValue, deleteForm, setNewForm, updateFields } from 'apps/auth/actions/formAction';
import { updatePageMessages } from 'apps/auth/actions/messageAction';
import { Button } from 'apps/auth/components/common/Button/Button';
import { FixedBottomPanel } from 'apps/auth/components/common/Layout/FixedBottomPanel/FixedBottomPanel';
import { HeaderPanel } from 'apps/auth/components/common/Layout/HeaderPanel/HeaderPanel';
import { Row } from 'apps/auth/components/common/Layout/Row';
import InputAutoDetectJs from 'apps/auth/components/form/InputAutoDetect';
import FormJs from 'apps/auth/components/form-multi/Form';
import { clearLeadingSpace, getEmptyMandatoryOrFieldsWithError, getField, getSortFields } from 'apps/auth/components/form-multi/utils';
import ServerSideDisclaimerJs from 'apps/auth/components/send/disclaimers/ServerSideDisclaimer';
import { transactionMethods } from 'apps/auth/components/utils/AmplitudeUtils';
import ModalComponent from 'apps/auth/components/utils/ModalComponent';
import ModalNewJs from 'apps/auth/components/utils/ModalNew';
import { OBQ_CONTINUE_CLICKED, OBQ_SCREEN_SHOWN } from 'apps/auth/constants/amplitude/AmplitudeEvents';
import {
    PAYMENT_COUNTRY,
    PAYMENT_CURRENCY,
    PAYMENT_METHOD_AMPLITUDE,
    PAYOUT_COUNTRY,
    PAYOUT_CURRENCY,
    PAYOUT_METHOD,
} from 'apps/auth/constants/amplitude/AmplitudeParamName';
import { QUESTIONNAIRE, QUESTIONNAIRE_DISCLAIMER, USE_QUESTIONNAIRE_SCREEN } from 'apps/auth/constants/ExtraNames';
import { ONBOARDING_QUESTIONNAIRE } from 'apps/auth/constants/formName';
import { AUTH_SEND_QUESTIONNAIRE_ACTION } from 'apps/auth/constants/UrlConstants';
import { useExtra, useFields, usePageMessages } from 'apps/auth/hooks/selectors';

type ModalRefHandle = {
    open?: (content?: React.ReactNode | null) => void;
    close?: () => void;
};

type QuestionnaireField = {
    name: string;
    visible?: boolean;
} & Record<string, unknown>;

type QuestionnaireDisclaimer = {
    title?: string;
    sections?: Record<string, unknown>[];
};

type SubmitResponse = {
    messages?: unknown;
    extra?: Record<string, unknown>;
    messageBodyMessages?: unknown[];
};

type AmplitudeInstance = {
    logEvent: (event: string, payload?: Record<string, unknown>) => void;
};

type Country = {
    nameEn?: string;
};

type Currency = {
    code?: string;
};

type PaymentForm = {
    paymentSourceFromMethod?: string;
};

type Precheck = {
    paymentMethod?: string;
};

const amplitudeInstance = amplitude as { getInstance: () => AmplitudeInstance };
const transactionMethodMap = transactionMethods as Record<string, string | undefined>;

const Form = FormJs as unknown as React.ComponentType<{
    action?: string;
    formName: string;
    getCanonicalPath?: boolean;
    onSubmitHandler?: (responseFields: unknown, json: SubmitResponse) => void;
    beforeSubmit?: () => void;
    children?: React.ReactNode;
}>;
const InputAutoDetect = InputAutoDetectJs as unknown as React.ComponentType<{
    formName: string;
    field: QuestionnaireField;
    setMaxLength?: boolean;
    dropdownModalOverflowHidden?: boolean;
}>;
const ModalNew = ModalNewJs as unknown as React.ForwardRefExoticComponent<
    React.RefAttributes<ModalRefHandle> & { getComponent?: (component: React.ReactNode) => React.ReactNode }
>;
const ServerSideDisclaimer = ServerSideDisclaimerJs as unknown as React.ComponentType<{
    content: QuestionnaireDisclaimer;
    title?: string;
    onButtonClick?: () => void;
    onCloseClick?: () => void;
    overflowHidden?: boolean;
}>;

type Questionnaire = {
    fields?: QuestionnaireField[];
    providerId?: number | string | null;
};

type OnboardingExtra = {
    deliveryMethod?: string;
    fromCountry?: Country;
    fromCurrency?: Currency;
    paymentForm?: PaymentForm;
    precheck?: Precheck;
    questionnaire?: Questionnaire;
    questionnaireDisclaimer?: QuestionnaireDisclaimer;
    toCountry?: Country;
    toCurrency?: Currency;
    userCountry?: Country;
};

type OnboardingPageMessages = {
    continueButton?: string;
    questionnaireTitle?: string;
    questionnaireSubTitle?: string;
    questionnaireDisclaimerTitle?: string;
};

interface OwnProps {
    modalRef: React.RefObject<ModalRefHandle | null>;
    onSubmitSuccess?: () => void;
}

interface DispatchProps {
    addExtras: (extra: Record<string, unknown>) => Promise<unknown>;
    updateExtraByName: (extraName: string, value: unknown) => Promise<unknown>;
    setNewForm: (formName: string, fields?: object, action?: string) => Promise<unknown>;
    updateFields: (formName: string, fields: unknown[]) => Promise<unknown>;
    deleteForm: (formName: string) => Promise<unknown>;
    updateMessages: (messages: unknown) => unknown;
    updatePageMessages: (messages: unknown) => unknown;
    changeFormFieldValue: (formName: string, value: unknown, fieldName: string) => unknown;
}

type OnboardingQuestionnaireModalProps = OwnProps & DispatchProps;

const FORM_NAME = ONBOARDING_QUESTIONNAIRE;
const FORM_ACTION = AUTH_SEND_QUESTIONNAIRE_ACTION;

const OnboardingQuestionnaireModal: React.FC<OnboardingQuestionnaireModalProps> = (props) => {
    const { modalRef } = props;
    const disclaimerRef = useRef<ModalRefHandle | null>(null);
    const shouldDeleteFormOnUnmount = useRef(false);
    const extra = useExtra<OnboardingExtra>();
    const { continueButton, questionnaireDisclaimerTitle, questionnaireTitle, questionnaireSubTitle } = usePageMessages<OnboardingPageMessages>() ?? {};
    const questionnaire = extra?.questionnaire;
    const disclaimer = extra?.questionnaireDisclaimer;
    const fields = (useFields(FORM_NAME) ?? {}) as Record<string, QuestionnaireField>;
    const payoutMethod = extra?.deliveryMethod;
    const amplitudeEventParams = {
        [PAYMENT_COUNTRY]: extra?.fromCountry?.nameEn,
        [PAYMENT_CURRENCY]: extra?.fromCurrency?.code,
        [PAYMENT_METHOD_AMPLITUDE]: extra?.precheck?.paymentMethod,
        [PAYOUT_COUNTRY]: extra?.toCountry?.nameEn,
        [PAYOUT_CURRENCY]: extra?.toCurrency?.code,
        [PAYOUT_METHOD]: payoutMethod ? transactionMethodMap[payoutMethod] : undefined,
    };

    useEffect(() => {
        amplitudeInstance.getInstance().logEvent(OBQ_SCREEN_SHOWN, amplitudeEventParams);
        if (Object.keys(fields).length === 0) {
            void props.setNewForm(FORM_NAME, {}, FORM_ACTION).then(() => {
                if (questionnaire?.fields?.length) {
                    const formFields: unknown[] = [...questionnaire.fields];
                    if (questionnaire.providerId != null) {
                        formFields.push(getField('prvId', String(questionnaire.providerId), '', false, false, '', 'hidden', false));
                    }
                    void props.updateFields(FORM_NAME, formFields);
                }
            });
        }
        return () => {
            if (shouldDeleteFormOnUnmount.current) {
                void props.deleteForm(FORM_NAME);
            }
        };
    }, []);

    const isSubmitDisabled = getEmptyMandatoryOrFieldsWithError(fields, false, false).length > 0;

    const closeModal = () => {
        modalRef?.current?.close?.();
    };

    const beforeSubmit = () => clearLeadingSpace(fields, FORM_NAME, props.changeFormFieldValue);

    const submitHandler = (_fields: unknown, json: SubmitResponse) => {
        if (json?.messages) {
            props.updatePageMessages(json.messages);
        }
        if (json?.extra) {
            void props.addExtras(json.extra);
        }
        if (json?.messageBodyMessages && json.messageBodyMessages.length > 0) {
            props.updateMessages(json.messageBodyMessages);
            modalRef?.current?.close?.();
            return;
        }
        props.onSubmitSuccess?.();
        void props.updateExtraByName(QUESTIONNAIRE, null);
        void props.updateExtraByName(QUESTIONNAIRE_DISCLAIMER, null);
        void props.updateExtraByName(USE_QUESTIONNAIRE_SCREEN, null);
        shouldDeleteFormOnUnmount.current = true;
        modalRef?.current?.close?.();
    };

    const openDisclaimer = () => {
        if (!disclaimer?.sections?.length) {
            return;
        }
        disclaimerRef.current?.open?.(
            <ServerSideDisclaimer
                content={disclaimer}
                title={disclaimer.title}
                onButtonClick={() => disclaimerRef.current?.close?.()}
                onCloseClick={() => disclaimerRef.current?.close?.()}
                overflowHidden
            />,
        );
    };

    return (
        <>
            <ModalComponent isIconClose hasBottomPanel modalRef={modalRef} onCloseIconClick={closeModal}>
                <HeaderPanel
                    title={questionnaireTitle}
                    subtitle={questionnaireSubTitle}
                    buttonRowType="button"
                    buttonRowCaption={questionnaireDisclaimerTitle}
                    buttonRowOnClick={openDisclaimer}
                />
                <Form action={FORM_ACTION} formName={FORM_NAME} getCanonicalPath={true} onSubmitHandler={submitHandler} beforeSubmit={beforeSubmit}>
                    {getSortFields(fields)
                        .filter((f: QuestionnaireField) => f?.visible)
                        .map((field: QuestionnaireField) => (
                            <Row indent="adaptive-24-32" key={field.name}>
                                <InputAutoDetect formName={FORM_NAME} field={field} setMaxLength dropdownModalOverflowHidden />
                            </Row>
                        ))}
                    <FixedBottomPanel isRightAligned>
                        <Button
                            type="submit"
                            raised={true}
                            disabled={isSubmitDisabled}
                            caption={continueButton}
                            onClick={() => amplitudeInstance.getInstance().logEvent(OBQ_CONTINUE_CLICKED, amplitudeEventParams)}
                        />
                    </FixedBottomPanel>
                </Form>
            </ModalComponent>
            <ModalNew ref={disclaimerRef} getComponent={(component) => component} />
        </>
    );
};

const actions = {
    addExtras,
    updateExtraByName,
    setNewForm,
    updateFields,
    deleteForm,
    updateMessages,
    updatePageMessages,
    changeFormFieldValue,
};

export default connect(null, actions)(OnboardingQuestionnaireModal as React.ComponentType<OnboardingQuestionnaireModalProps>);
